import React, { FC, useEffect, useCallback, useState } from "react";
import classNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Review as ReviewInterface } from "@app/components/base-page/models";
import { DELAY_MS, MIN_SLIDES_TO_DISPLAY_NAVIGATION } from "./constants";
import { Review } from "./components/review/Review";

type Props = {
  reviews: ReviewInterface[];
};

export const Carousel: FC<Props> = ({ reviews }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({
      delay: DELAY_MS,
      stopOnInteraction: false,
      stopOnFocusIn: false,
    }),
  ]);

  const onDotClick = (index: number) => {
    if (index !== activeSlide) {
      emblaApi?.plugins()?.autoplay?.reset();
      emblaApi?.scrollTo(index);
      setActiveSlide(index);
    }
  };

  const onEmblaEvent = useCallback((emblaApi: EmblaCarouselType) => {
    emblaApi?.plugins()?.autoplay?.play();
    setActiveSlide(emblaApi?.selectedScrollSnap());
  }, []);

  useEffect(() => {
    emblaApi?.on("select", onEmblaEvent);

    return () => {
      emblaApi?.off("select", onEmblaEvent);
    };
  }, [emblaApi, onEmblaEvent]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {reviews.map((review) => (
            <div key={review._uid} className="min-w-0 flex-0-100">
              <Review data={review} />
            </div>
          ))}
        </div>
      </div>
      {reviews.length >= MIN_SLIDES_TO_DISPLAY_NAVIGATION ? (
        <div
          className={classNames(
            "absolute bottom-0 mb-8 flex w-full items-end justify-center",
            "xxl:mb-13",
          )}
        >
          <div className={classNames("flex gap-2", "xxl:gap-4")}>
            {reviews.map((review, index) => (
              <button
                key={review._uid}
                onClick={() => onDotClick(index)}
                className={classNames(
                  "h-1.5 overflow-hidden rounded-full bg-grey-dark duration-1000",
                  "xxl:h-2.5",
                  {
                    "w-14 xxl:w-22": index === activeSlide,
                    "w-1.5 xxl:w-2.5": index !== activeSlide,
                  },
                )}
              >
                {index === activeSlide ? (
                  <div
                    className={classNames(
                      "h-full w-full origin-left animate-fill-width rounded-full bg-primary-content",
                    )}
                  />
                ) : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
